import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Plus, Calendar, User, ArrowRight, MessageSquare, History } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PRIORITY_STYLES, STATUS_STYLES, timeAgo } from "../components/shared";

const COLUMNS = [
  { key: "open", label: "Open", tone: "bg-blue-500" },
  { key: "in_progress", label: "In Progress", tone: "bg-amber-500" },
  { key: "closed", label: "Closed", tone: "bg-emerald-500" },
];
const NEXT = { open: "in_progress", in_progress: "closed", closed: "open" };

export default function Actions() {
  const [actions, setActions] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", owner: "", due_date: "" });
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveNote, setMoveNote] = useState("");
  const [moving, setMoving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/actions").then((r) => setActions(r.data));
  useEffect(() => { load(); }, []);

  const openMove = (a) => { setMoveTarget({ action: a, next: NEXT[a.status] }); setMoveNote(""); };
  const confirmMove = async () => {
    if (!moveNote.trim()) return;
    const { action, next } = moveTarget;
    setMoving(true);
    try {
      const { data } = await api.patch(`/actions/${action.id}`, { status: next, comment: moveNote });
      setActions((prev) => prev.map((x) => (x.id === action.id ? data : x)));
      toast.success(`Moved to ${COLUMNS.find((c) => c.key === next).label}`);
      setMoveTarget(null);
    } catch { toast.error("Failed to move action"); }
    finally { setMoving(false); }
  };

  const create = async () => {
    if (!form.title) return;
    const { data } = await api.post("/actions", { ...form, due_date: form.due_date ? new Date(form.due_date).toISOString() : null });
    setActions((prev) => [data, ...prev]);
    toast.success("Action created");
    setOpen(false);
    setForm({ title: "", description: "", priority: "medium", owner: "", due_date: "" });
  };

  if (!actions) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="Action Management" subtitle="Kanban board of every corrective and preventative action.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="new-action-btn"><Plus className="mr-2 h-4 w-4" /> New Action</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Create Action</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="action-title" /></div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="action-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="action-priority"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Owner</Label><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Assignee" data-testid="action-owner" /></div>
              </div>
              <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="action-due" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full" data-testid="action-save-btn">Create Action</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {COLUMNS.map((col) => {
          const items = actions.filter((a) => a.status === col.key);
          return (
            <div key={col.key} className="rounded-2xl bg-secondary/40 p-3">
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span className={`h-2.5 w-2.5 rounded-full ${col.tone}`} /> {col.label}
                </div>
                <Badge variant="secondary" className="rounded-full">{items.length}</Badge>
              </div>
              <div className="space-y-2.5" data-testid={`kanban-col-${col.key}`}>
                {items.map((a) => (
                  <motion.div key={a.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Card onClick={() => setDetail(a)} data-testid={`action-card-${a.id}`} className="group cursor-pointer rounded-xl border-border p-4 transition-shadow hover:shadow-md">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium leading-snug">{a.title}</div>
                        <Badge variant="outline" className={`shrink-0 rounded-full border text-[10px] ${PRIORITY_STYLES[a.priority]}`}>{a.priority}</Badge>
                      </div>
                      {a.source && a.source !== "manual" && (
                        <Badge variant="secondary" className="mt-2 rounded-full text-[10px] capitalize">{a.source}</Badge>
                      )}
                      <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{a.owner}</span>
                        {a.due_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(a.due_date).toLocaleDateString()}</span>}
                        {a.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{a.comments.length}</span>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); openMove(a); }} data-testid={`move-action-${a.id}`}
                        className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg bg-secondary py-1.5 text-xs font-medium text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100">
                        Move to {COLUMNS.find((c) => c.key === NEXT[a.status]).label} <ArrowRight className="h-3 w-3" />
                      </button>
                    </Card>
                  </motion.div>
                ))}
                {items.length === 0 && <div className="py-8 text-center text-xs text-muted-foreground">No actions</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Move to {moveTarget && COLUMNS.find((c) => c.key === moveTarget.next).label}</DialogTitle>
          </DialogHeader>
          {moveTarget && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Add a note to record why <span className="font-medium text-foreground">"{moveTarget.action.title}"</span> is moving to{" "}
                <span className="font-medium text-foreground">{COLUMNS.find((c) => c.key === moveTarget.next).label}</span>. A note is required.
              </p>
              <div className="space-y-1.5">
                <Label>Note <span className="text-destructive">*</span></Label>
                <Textarea rows={3} value={moveNote} onChange={(e) => setMoveNote(e.target.value)}
                  placeholder="e.g. Work started, materials ordered, verified complete…" data-testid="move-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button disabled={!moveNote.trim() || moving} onClick={confirmMove} className="rounded-full" data-testid="move-confirm-btn">
              {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={`rounded-full border ${PRIORITY_STYLES[detail.priority]}`}>{detail.priority}</Badge>
                  <Badge className={`rounded-full capitalize ${STATUS_STYLES[detail.status]}`}>{detail.status.replace("_", " ")}</Badge>
                  {detail.source && detail.source !== "manual" && <Badge variant="secondary" className="rounded-full capitalize">{detail.source}{detail.source_ref ? ` · ${detail.source_ref}` : ""}</Badge>}
                </div>
                <DialogTitle className="pt-1 text-left font-heading">{detail.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Owner</div><div className="font-medium">{detail.owner}</div></div>
                  <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Due date</div><div className="font-medium">{detail.due_date ? new Date(detail.due_date).toLocaleDateString() : "—"}</div></div>
                </div>
                {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}
                <div className="border-t border-border pt-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <History className="h-4 w-4" /> Note history ({detail.comments?.length || 0})
                  </div>
                  <div className="max-h-64 space-y-2 overflow-y-auto pr-1" data-testid="action-note-history">
                    {(!detail.comments || detail.comments.length === 0) && (
                      <div className="rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">No notes yet. Notes are recorded each time this action is moved.</div>
                    )}
                    {[...(detail.comments || [])].reverse().map((c, i) => (
                      <div key={i} className="rounded-lg border border-border bg-card px-3 py-2">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">{c.author}</span><span>{timeAgo(c.at)}</span>
                        </div>
                        <div className="mt-1 text-sm">{c.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
